import { BatchManager } from './batchManager';
import { getPageURLFromBrowser, setCommonAttributes } from './commonAttributes';
import { trackCustomPageview, trackPageview } from './eventTrackers/pageView';
import getUrlToObserve from '../utils/getUrlToObserve';
import { log } from '../utils/logger';
import { compareURLCustomPageview, validateCustomEventData } from '../utils/customEventValidationUtils';
import { isValidUrl } from '../utils/urlParamUtils';
import { CustomEventData } from '../constants/customEventDataModel';
import { trackCustomEvent } from './eventTrackers/customEvent';
import { isChinaRegion } from '../utils/env-utils';

/**
 * Adds an observer to watch for meaningful changes to the URL
 * @return a testing-only callback function that allows setting previousUrl
 */
export function addUrlChangeListener(): (b: string) => void {
  let previousUrl = getUrlToObserve(getPageURLFromBrowser());
  const setUrl: (b: string) => void = (url) => {
    previousUrl = url;
  };

  const observer = new MutationObserver(function () {
    trackUrlChanges(previousUrl, getPageURLFromBrowser(), setUrl);
  });

  const config = { subtree: true, childList: true };
  observer.observe(document, config);

  return setUrl;
}

/**
 * NOTE: Exported for testing purposes only - treat as a private method in production code
 * Compares the previous URL to a cleaned version of the new URL, and tracks a pageview if they differ
 * Also updates variable containing the previous URL
 * @param previousUrl - The previous value of the page URL.  Should already be sanitized.
 * @param newUrlRaw - The new page URL, unsanitized.
 * @param setUrl - A function that takes a string and sets the previous URL to it
 */
export function trackUrlChanges(previousUrl: string, newUrlRaw: string, setUrl: (b: string) => void): void {
  const newUrl = getUrlToObserve(newUrlRaw);
  if (newUrl !== previousUrl) {
    trackPageview();
    log(`URL changed to ${location.href}`);
    setUrl(newUrl);
  }
}

/**
 * Watches for when the page becomes invisible and fires off all the remaining events in the Data Layer
 * @param batchManager - a reference to the BatchManager singleton
 * @param selector - not currently in use, but will be necessary to calculate the upcoming scrollDepth events
 */
export function addVisibilityChangeListener(batchManager: BatchManager, selector?: string) {
  document.addEventListener('visibilitychange', (e: Event) => {
    if (document.visibilityState === 'hidden') {
      // Note: once we've added scrollDepth events, we'll trigger those here
      batchManager.flushBatchQueue();
    }
  });
}
/**
 * Adds event listener for TRIGGER_EVENT which calls triggerEventHandler when the TRIGGER_EVENT event occurs
 */
export function addTriggerEventListener() {
  document.addEventListener(AWSMA.TRIGGER_EVENT, (e: any) => {
    triggerEventHandler(e.detail as CustomEventData);
  });
}

/**
 * Validates the customEventData parameter as being of CustomEventData type then sets the common attributes and calls the respective custom event function
 * @param customEventData the information about the custom event, note that param customEventData is not yet known to be a valid CustomEventData
 */
export function triggerEventHandler(customEventData: CustomEventData) {
  if (!validateCustomEventData(customEventData)) {
    console.error('AWSD2C: Invalid customEventData config, will not upload custom event');

    return;
  }

  if (customEventData.event.type === 'pageview') {
    if (compareURLCustomPageview(customEventData)) {
      return;
    }

    setCommonAttributes(customEventData);
    trackCustomPageview(customEventData);
  } else {
    // This is for custom events that are not custom pageviews
    if (isChinaRegion()) {
      setCommonAttributes(customEventData);
      trackCustomEvent(customEventData);
    }
  }
}

/**
 * Runs the custom event flow for events that were caught by d2c-loader before the client loaded
 */
export function handleEventsFromBeforeClientLoaded(): void {
  for (const e of AWSMA.eventQueue) {
    triggerEventHandler(e.detail as CustomEventData);
  }
}
